/**
 * DSL_Socials
 * Social media QR codes component
 */

import { useMemo, memo, useRef, useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { FaLinkedin, FaInstagram, FaYoutube } from 'react-icons/fa'
import { trackEvent } from '../../utils/analytics'
import { getMediaQueryKey } from '../../utils/deviceDetection'

const HUD_COLOR = '#EE3A17'

// Memoized QR code component to prevent regeneration
const MemoizedQRCode = memo(function MemoizedQRCode({ url, size }) {
  return (
    <QRCodeSVG
      value={url}
      size={size}
      level="H"
      includeMargin={false}
      fgColor="#EE3A17"
      bgColor="transparent"
    />
  )
})

export default function DSL_Socials({ isTransitioning }) {
  // 4K detection
  const [is4K, setIs4K] = useState(() => window.matchMedia('(min-width: 2560px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 2560px)')
    const handler = (e) => setIs4K(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const qrSize = is4K ? 260 : 180
  const iconSize = is4K ? 51 : 34

  // Memoize socials array to prevent recreation
  const socials = useMemo(() => [
    {
      name: 'LINKEDIN',
      url: 'https://www.linkedin.com/company/darkstarlabs/',
      Icon: FaLinkedin
    },
    {
      name: 'INSTAGRAM',
      url: 'https://www.instagram.com/darkstarlabs.io/',
      Icon: FaInstagram
    },
    {
      name: 'YOUTUBE',
      url: 'https://www.youtube.com/@DarkStarLabs',
      Icon: FaYoutube
    }
  ], [])
  
  // Memoize empty style object
  const emptyStyle = useMemo(() => ({}), [])
  
  // Memoize cursor style to prevent object creation
  const cursorPointerStyle = useMemo(() => ({ cursor: 'pointer' }), [])

  const isMobilePortrait = getMediaQueryKey() === 'mobilePortrait'

  return (
    <div className="dsl-socials-container">
      {socials.map((social, index) => {
        const IconComponent = social.Icon
        const flickerDelay = index * 0.15
        
        // Memoize flicker style object per index to prevent recreation
        const flickerStyle = useMemo(() => ({
          '--flicker-delay-1': `${flickerDelay}s`,
          '--flicker-delay-2': `${flickerDelay + 0.05}s`,
          '--flicker-delay-3': `${flickerDelay + 0.1}s`
        }), [flickerDelay])
        
        return (
          <div 
            key={social.name}
            className={`dsl-social-item ${isTransitioning ? 'flickering' : ''}`}
            style={isTransitioning ? flickerStyle : emptyStyle}
          >
            <h3 className="dsl-social-label">{social.name}</h3>
            {isMobilePortrait ? (
              /* Mobile portrait: just the icon, no QR */
              <div
                onClick={() => {
                  trackEvent(`${social.name.toLowerCase()}_click`)
                  window.open(social.url, '_blank')
                }}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px'
                }}
              >
                <IconComponent size={56} color="#EE3A17" />
              </div>
            ) : (
              /* Desktop/tablet: full QR wrapper */
              <div 
                className="dsl-qr-wrapper"
              >
                <div className="dsl-qr-background" />
                <QRFrame size={qrSize} />
                <div className="dsl-qr-code">
                  <MemoizedQRCode url={social.url} size={qrSize} />
                </div>
                <div className="dsl-qr-icon">
                  <IconComponent size={iconSize} color="#EE3A17" />
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// QRFrame accepts a size prop so it can scale at 4K
const QRFrame = memo(function QRFrame({ size = 200 }) {
  const cornerSize = 20
  
  return (
    <svg className="dsl-qr-frame" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Top left corner */}
      <g>
        <line x1="0" y1="0" x2="0" y2={cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1="0" y1="0" x2={cornerSize} y2="0" stroke={HUD_COLOR} strokeWidth="2" />
      </g>
      
      {/* Top right corner */}
      <g>
        <line x1={size} y1="0" x2={size} y2={cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1={size} y1="0" x2={size - cornerSize} y2="0" stroke={HUD_COLOR} strokeWidth="2" />
      </g>
      
      {/* Bottom left corner */}
      <g>
        <line x1="0" y1={size} x2="0" y2={size - cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1="0" y1={size} x2={cornerSize} y2={size} stroke={HUD_COLOR} strokeWidth="2" />
      </g>
      
      {/* Bottom right corner */}
      <g>
        <line x1={size} y1={size} x2={size} y2={size - cornerSize} stroke={HUD_COLOR} strokeWidth="2" />
        <line x1={size} y1={size} x2={size - cornerSize} y2={size} stroke={HUD_COLOR} strokeWidth="2" />
      </g>
    </svg>
  )
})
